import { applyColor }from '~src/style/ColorService';
import React = require('react');

import DesignTokens from '@tableau/design-tokens';

import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { SiteRolesHelper } from '~src/siteManagement/helpers/SiteRolesHelper';

import { Messages } from '../../../../codegen/Localize';
import { SiteRoles } from '../../../users/serverApi/types/ISiteRole';
import { IGetGroupDetailInfoResult } from '../../models/IGroupInfo';
import { GrantLicenseModes } from '../../serverApi/requests/SyncActiveDirectoryGroupRequest';

export interface GroupDetailsSubspaceProps {
  readonly group: IGetGroupDetailInfoResult;
}

interface GroupDetailsSubspaceState {
  isSavingMinimumSiteRole: boolean;
  isChangingMinimumSiteRoleDetails: boolean;
  groupDetailsModel: IGroupDetailsModel;
  licenseUponLoginCheckboxDisabled: boolean;
  externalUserEnabled?: boolean;
}

export interface IGroupDetailsModel {
  domainName: string;
  createdAt: string;
  updatedAt: string;
  isAllUsersGroup: boolean;
  minimumSiteRole?: string;
  grantLicenseMode?: GrantLicenseModes;
  externalUserEnabled?: boolean;
}

export class GroupDetailsSubspaceComponent extends React.Component<GroupDetailsSubspaceProps, GroupDetailsSubspaceState> {

  private isLocalGroup: boolean = !(this.props.group.domainName && this.props.group.domainName !== 'local');
  private originalGroupDetails: IGetGroupDetailInfoResult = this.props.group;

  public constructor(props: GroupDetailsSubspaceProps) {
    super(props);
    this.state = {
      isSavingMinimumSiteRole: false,
      isChangingMinimumSiteRoleDetails: false,
      groupDetailsModel: {
        domainName: this.props.group.domainName,
        createdAt: this.props.group.createdAt,
        updatedAt: this.props.group.updatedAt,
        isAllUsersGroup: this.props.group.isAllUsersGroup,
        minimumSiteRole: (!this.props.group.minimumSiteRole) ?
          SiteRoles.Unlicensed : this.props.group.minimumSiteRole,
        grantLicenseMode: this.props.group.grantLicenseMode,
        externalUserEnabled: this.props.group.externalUserEnabled
      },
      licenseUponLoginCheckboxDisabled: (!this.isLocalGroup && this.props.group.minimumSiteRole === SiteRoles.Unlicensed)
    };
  }

  public render() {
    const isExternalUserEnabledForSite = getSessionManagementServiceInstance().isExternalUserEnabledForSite();
    return (
      <div className="tb-details" style={this.DetailsContainerStyle}>
        <table style={this.DetailsTableStyle}>
          <thead>
            <tr>
              <th style={{...this.DetailsHeaderStyle, textAlign: 'right'}}>{Messages.groupDetailHeader()}</th>
              <th style={this.DetailsValueStyle}>{Messages.column_metrics_value()}</th>
            </tr>
          </thead>
          <tbody>
            {this.isLocalGroup && this.renderRow(Messages.column_domainName(), this.originalGroupDetails.domainName, 'domain')}
            {isExternalUserEnabledForSite && this.getExternalUserEnabled()}
            {this.getSiteRole()}
          </tbody>
        </table>
      </div>
    );
  }

  private getExternalUserEnabled = (): JSX.Element => {
    let externalUserStr = this.state.groupDetailsModel.externalUserEnabled ? Messages.groupEphemeralUsersAllowed(): Messages.groupExternalUserNotAllowed();
    return this.renderRow(Messages.groupEphemeralUsers(), externalUserStr, 'externalUser');
  };

  private getSiteRole = (): JSX.Element => {
    let isEnabled = this.state.groupDetailsModel.grantLicenseMode;
    let minimumSiteRole = this.isLocalGroup && this.state.groupDetailsModel.minimumSiteRole === SiteRoles.Unlicensed ?
    SiteRoles.ExplorerCanPublish : this.state.groupDetailsModel.minimumSiteRole;
    let grantRoleStr = isEnabled ? minimumSiteRole: Messages.groupGrantRoleNotSet();
    return this.renderRow(Messages.groupsMinimumSiteRole(), SiteRolesHelper.siteRoleToName(grantRoleStr), 'grantRole');
  };

  private renderRow = (label: string, value: string, testIdValue: string): JSX.Element => {
    let testStr = 'tb-' + testIdValue + '-value-id';
    return(
      <tr>
      <td className="tb-label" style={this.DetailsCellStyle}>{label}</td>
      <td className="tb-details-value" tb-test-id={testStr} style={this.DetailsCellStyle}>{value}</td>
      </tr>
    );
  };

  // WCAG 1.4.10 Reflow: container caps the table to its parent's width so the
  // table can never push a horizontal scrollbar onto an outer container at
  // 400% zoom / 320 CSS px viewport.
  private DetailsContainerStyle: React.CSSProperties = {
    maxWidth: '100%',
    overflowX: 'hidden',
  };

  // table-layout:fixed + width:100% lets cells share the available width
  // proportionally rather than each demanding its content's natural width.
  private DetailsTableStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '100%',
    tableLayout: 'fixed',
  };

  // Cells must allow long values (long site-role names, domain strings, etc.)
  // to wrap rather than push the column wider.
  private DetailsCellStyle: React.CSSProperties = {
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  };

  private DetailsHeaderStyle: React.CSSProperties = {
    padding: '12px',
    borderBottom: 'none',
    fontWeight: 'bold',
    color: applyColor(DesignTokens.color.font.default.subtle, '#939393')
  };
  private DetailsValueStyle: React.CSSProperties = {
    padding: '12px',
    borderBottom: 'none',
    fontWeight: 'bold',
    color: applyColor(DesignTokens.color.font.default.default, '#2e2e2e')
  };
}

export const GroupDetailsSubspace: React.ComponentType<GroupDetailsSubspaceProps> = GroupDetailsSubspaceComponent;
